import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import billingService from '../../api/billingService';
import customerService from '../../api/customerService';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { fromLocalDateTimeInputToISO } from '../../utils/dateUtils';
import CompactPagination from '../../components/billing/CompactPagination';
import RenewalModal from '../../components/billing/RenewalModal';
import { can } from '../../Utility/Utility';

const SubscriptionList = () => {
  const navigate = useNavigate();
  
  const [subscriptions, setSubscriptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(10);
  const [userRole, setUserRole] = useState('owner');
  const [tableActions, setTableActions] = useState({});
  const [allFollowups, setAllFollowups] = useState([]);
  const [followupsLoading, setFollowupsLoading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadRecords, setUploadRecords] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [productId, setProductId] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all'); // 'all', 'expired', 'upcoming'
  const [feedbackFilter, setFeedbackFilter] = useState('');
  const [usageFilter, setUsageFilter] = useState('');


  const [showFollowupModal, setShowModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [feedbackHistory, setFeedbackHistory] = useState([]);
  const [followupData, setFollowupData] = useState({
    feedback: 'satisfied',
    usage: 'using',
    comment: '',
    schedule_followup: false,
    followup_date: ''
  });

  useEffect(() => {
    fetchInitialData();
    fetchMembers();
  }, []);

  useEffect(() => {
    fetchSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, status, productId, expiryFilter, assignTo]);

  useEffect(() => {
    fetchAllFollowups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptions, assignTo]);

  const fetchAllFollowups = async () => {
    try {
      if (subscriptions.length === 0) {
        setAllFollowups([]);
        return;
      }
      setFollowupsLoading(true);
      const uniqueSubIds = Array.from(new Set(subscriptions.map(s => String(s._id)))).filter(Boolean);
      const all = [];
      const sessionRes = await customerService.getSession();
      const userInfo = sessionRes?.userInfo;
      const forceAssignTo = userInfo && userInfo.role !== 'owner' ? userInfo.email : '';
      for (let i = 0; i < uniqueSubIds.length; i++) {
        try {
          const subId = uniqueSubIds[i];
          const params = { subscription_id: subId, status: 'all', limit: 5000 };
          if (forceAssignTo) params.assignTo = forceAssignTo;
          const res = await billingService.getFollowups(params);
          if (res.success && Array.isArray(res.data)) {
            for (const f of res.data) {
              all.push(f);
            }
          }
        } catch (err) {
          console.error('Error fetching followups for sub', uniqueSubIds[i], err.message);
        }
      }
      setAllFollowups(all);
    } catch (error) {
      console.error('fetchAllFollowups top-level error:', error.message);
    } finally {
      setFollowupsLoading(false);
    }
  };

  const getFollowupsForSub = (subId) => {
    const sid = String(subId);
    return allFollowups.filter(f => {
      const fSid = String(f.subscription_id || f.subscriptionId || '');
      return fSid === sid;
    });
  };

  const sortScheduledFollowups = (arr) => {
    return arr
      .filter(f => f.schedule_followup === true && f.followup_date)
      .map(f => ({ ...f, _ts: new Date(f.followup_date).getTime() }))
      .filter(f => !isNaN(f._ts))
      .sort((a, b) => a._ts - b._ts);
  };

  const sortCompletedFollowups = (arr) => {
    return arr
      .filter(f => f.status === 'completed' || f.feedback === 'completed' || f.schedule_followup === false)
      .filter(f => (f.completedAt || f.updated_at || f.created_at))
      .map(f => ({ ...f, _ts: new Date(f.completedAt || f.updated_at || f.created_at).getTime() }))
      .filter(f => !isNaN(f._ts))
      .sort((a, b) => b._ts - a._ts);
  };

  const getNextFollowup = (list) => {
    const sorted = sortScheduledFollowups(list);
    const now = Date.now();
    let next = sorted.find(f => f._ts >= now);
    let overdueBefore = sorted.filter(f => f._ts < now);
    if (!next && overdueBefore.length > 0) {
      overdueBefore.sort((a, b) => a._ts - b._ts);
      next = overdueBefore[0];
    }
    return next || null;
  };

  const getLastCompleted = (list) => {
    const sorted = sortCompletedFollowups(list);
    return sorted[0] || null;
  };

  const fetchMembers = async () => {
    try {
      const sessionRes = await customerService.getSession();
      if (sessionRes && sessionRes.success) {
        const userInfo = sessionRes.userInfo;
        setUserRole(userInfo.role);
        setTableActions(userInfo.permissions?.tableActions?.subscriptions || {});
        if (userInfo.role !== 'owner') {
          setAssignTo(userInfo.email);
        }
        const response = await customerService.getAllMembers(userInfo.id);
        if (response && response.data && response.data.ActiveMembers) {
          setMembers(response.data.ActiveMembers);
        }
      }
    } catch (error) {
      console.error('Error fetching members:', error);
    }
  };

  const fetchInitialData = async () => {
    try {
      const res = await billingService.getProducts();
      if (res.success) setProducts(res.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const res = await billingService.getSubscriptions({ 
        page, 
        limit, 
        search, 
        status, 
        product_id: productId,
        assignTo,
        expired: expiryFilter === 'expired',
        upcoming: expiryFilter === 'upcoming',
        feedback: feedbackFilter,
        usage: usageFilter
      });
      
      if (res.success) {
        setSubscriptions(res.data);
        setTotal(res.total);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFollowup = async (sub) => {
    setSelectedSub(sub);
    setFeedbackHistory([]); // Reset history
    setShowModal(true);
    
    // Fetch feedback history for this subscription (all statuses, full history)
    try {
      const sessionRes = await customerService.getSession();
      const userInfo = sessionRes?.userInfo;
      const forceAssignTo = userInfo && userInfo.role !== 'owner' ? userInfo.email : '';
      const params = { subscription_id: sub._id, status: 'all', limit: 5000 };
      if (forceAssignTo) params.assignTo = forceAssignTo;
      const res = await billingService.getFollowups(params);
      if (res.success) {
        setFeedbackHistory(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching feedback history:', error);
    }
  };

  const handleOpenRenewal = (sub) => {
    setSelectedSub(sub);
    setShowRenewalModal(true);
  };

  const handleDelete = async (sub) => {
    const customerName = [sub.customerData?.firstname, sub.customerData?.lastname].filter(Boolean).join(' ')
      || sub.customer?.company || 'this subscription';
    if (!window.confirm(`Are you sure you want to delete the sales record for "${customerName}"? This action cannot be undone.`)) {
      return;
    }
    try {
      const res = await billingService.deleteSubscription(sub._id);
      if (res.success) {
        await fetchSubscriptions();
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handleFollowupSubmit = async (e) => {
    e.preventDefault();
    try {
      const sessionRes = await customerService.getSession();
      const userInfo = sessionRes?.userInfo;
      const payload = {
        subscription_id: selectedSub._id,
        customer_id: typeof selectedSub.customer_id === 'object' ? selectedSub.customer_id._id : selectedSub.customer_id,
        ...followupData,
        ...(followupData.schedule_followup && followupData.followup_date && { followup_date: fromLocalDateTimeInputToISO(followupData.followup_date) })
      };
      if (userInfo && userInfo.role !== 'owner') {
        payload.assignTo = userInfo.email;
      }
      await billingService.createFollowup(payload);
      setShowModal(false);
      await fetchAllFollowups();
      alert('Follow-up record saved and customer data synced!');
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchSubscriptions();
  };

  const exportToExcel = async () => {
    try {
      const res = await billingService.getSubscriptions({ 
        limit: 5000, 
        search, 
        status, 
        product_id: productId,
        assignTo,
        expired: expiryFilter === 'expired',
        upcoming: expiryFilter === 'upcoming',
        feedback: feedbackFilter,
        usage: usageFilter
      });
      
      if (res.success && res.data) {
        const exportData = res.data.map(s => ({
          customerId: s.customer_id?._id || s.customer_id || '',
          next_renewal_date: s.next_renewal_date ? new Date(s.next_renewal_date).toISOString().split('T')[0] : '',
          product_name: s.product?.productName || s.product_name || '',
          start_date: s.start_date ? new Date(s.start_date).toISOString().split('T')[0] : '',
          alias: s.custom_alias || '',
          comment: s.comment || '',
          sales_date: s.sales_date ? new Date(s.sales_date).toISOString().split('T')[0] : '',
          service_details: s.service_details || ''
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales");
        XLSX.writeFile(wb, `Sales_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export sales data');
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (evt) => {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: 'binary' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        setUploadRecords(data);
      };
      reader.readAsBinaryString(file);
    }
  };

  const handleUploadSubmit = async () => {
    try {
      setIsUploading(true);
      const res = await billingService.uploadSales(uploadRecords);
      if (res.success) {
        alert('Sales upload started! You can check progress in System Logs.');
        setShowUploadModal(false);
        setUploadRecords([]);
        navigate('/system-logs');
      }
    } catch (error) {
      alert(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const downloadSample = () => {
    const sampleData = [
      {
        customerId: '65e... (Get from Export)',
        next_renewal_date: '2025-01-01',
        product_name: 'SaaS Basic',
        start_date: '2024-01-01',
        alias: 'Custom Service Name',
        comment: 'Customer requested weekday support only',
        sales_date: '2024-01-01',
        service_details: 'Standard service details'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sample Format");
    XLSX.writeFile(wb, "Sales_Upload_Sample.xlsx");
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-700 mb-1">Sales</h4>
          <p className="text-muted small mb-0">Manage recurring services and active plans.</p>
        </div>
        <div className="d-flex gap-2">
          <button
            className="btn btn-light border shadow-sm px-4"
            onClick={() => navigate('/ticket-mappings')}
          >
            <i className="bi bi-ticket-detailed me-2"></i> Ticket Mapping
          </button>
          {((userRole === 'owner' || tableActions.upload !== false) && can('uploadFiles')) && (
            <button className="btn btn-light border shadow-sm px-4" onClick={() => setShowUploadModal(true)}>
              <i className="bi bi-upload me-2"></i> Upload
            </button>
          )}
          {((userRole === 'owner' || tableActions.export !== false) && can('exportFiles')) && (
            <button className="btn btn-light border shadow-sm px-4" onClick={exportToExcel}>
              <i className="bi bi-download me-2"></i> Export Excel
            </button>
          )}
          {can('addSales') && (
            <button 
              className="btn btn-primary shadow-sm px-4"
              onClick={() => navigate('/billing/subscriptions/create')}
            >
              <i className="bi bi-plus-lg me-2"></i> New Subscription
            </button>
          )}
        </div>
      </div>

      <div className="card border-0 shadow-sm mb-4">
        <div className="card-header border-0 bg-transparent pt-4 px-4">
          <form className="row g-3" onSubmit={handleSearch}>
            <div className="col-md-3">
              <div className="input-group">
                <span className="input-group-text bg-light border-0"><i className="bi bi-search"></i></span>
                <input 
                  type="text" 
                  className="form-control bg-light border-0" 
                  placeholder="Search..." 
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-3">
              <select className="form-select bg-light border-0" value={productId} onChange={e => { setProductId(e.target.value); setPage(1); }}>
                <option value="">All Products</option>
                {products.map(p => (
                  <option key={p._id} value={p._id}>{p.productName}</option>
                ))}
              </select>
            </div>
            <div className="col-md-2">
              <select className="form-select bg-light border-0" value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
                <option value="expired">Expired</option>
              </select>
            </div>
            <div className="col-md-2">
              <select 
                className="form-select bg-light border-0" 
                value={expiryFilter} 
                onChange={e => { setExpiryFilter(e.target.value); setPage(1); }}
              >
                <option value="all">All Expiry</option>
                <option value="expired">Expired Only</option>
                <option value="upcoming">Upcoming (30 Days)</option>
              </select>
            </div>
            <div className="col-md-2">
              <select 
                className="form-select bg-light border-0" 
                value={feedbackFilter} 
                onChange={e => { setFeedbackFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Feedback</option>
                <option value="satisfied">Satisfied</option>
                <option value="disatisfied">Disatisfied</option>
              </select>
            </div>
            <div className="col-md-2">
              <select 
                className="form-select bg-light border-0" 
                value={usageFilter} 
                onChange={e => { setUsageFilter(e.target.value); setPage(1); }}
              >
                <option value="">All Usage</option>
                <option value="using">Using</option>
                <option value="not using">Not Using</option>
              </select>
            </div>
            <div className="col-md-2">
              <select 
                className="form-select bg-light border-0" 
                value={assignTo} 
                onChange={e => { setAssignTo(e.target.value); setPage(1); }}
                disabled={userRole !== 'owner'}
              >
                <option value="">All Members</option>
                {members.map(member => (
                  <option key={member.username} value={member.username}>{member.username}</option>
                ))}
              </select>
            </div>
            <div className="col-md-1">
              <button type="submit" className="btn btn-light w-100">Filter</button>
            </div>
          </form>
        </div>
        
        <div className="table-responsive mt-3">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Customer / Service</th>
                <th>Product</th>
                <th>Dates</th>
                <th>Feedback</th>
                <th>Followup</th>
                <th>Amount</th>
                <th>Status</th>
                <th className="text-end">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="9" className="text-center py-5">Loading...</td></tr>
              ) : subscriptions.length === 0 ? (
                <tr><td colSpan="9" className="text-center py-5">No subscriptions found.</td></tr>
              ) : (
                subscriptions.map((sub) => (
                  <tr key={sub._id}>
                    <td>
                      <div
                        className="fw-700 text-dark cursor-pointer"
                        onClick={() => navigate(`/billing/customers/${sub.customer_id}`)}
                      >
                        {sub.customerData?.company || sub.customer?.company || 'No Company'}
                      </div>
                      <div
                        className="text-xs text-primary fw-600 cursor-pointer"
                        onClick={() => navigate(`/billing/customers/${sub.customer_id}`)}
                      >
                        {[
                          sub.customerData?.firstname || sub.customer?.firstname || '',
                          sub.customerData?.lastname || sub.customer?.lastname || ''
                        ].join(' ').trim() || sub.customerData?.contactPerson || sub.customer?.contactPerson || 'Unknown Customer'}
                      </div>
                      <div className="text-xs text-muted">{sub.customerData?.phone || sub.customer?.phone}</div>
                      <div className="text-xs text-primary fw-600 mt-1">{sub.custom_alias || sub.productData?.productName || sub.product?.productName}</div>
                    </td>
                    <td>
                      <div className="text-xs fw-600">{sub.productData?.productName || sub.product?.productName}</div>
                      <div className="text-xs text-muted">Qty: {sub.quantity}</div>
                    </td>
                    <td>
                      <div className="text-xs">Start: {formatDate(sub.start_date)}</div>
                      {(() => {
                        const product = sub.productData || sub.product || {};
                        const isRenewable = product.isRenewable ?? product.isRenewal ?? product.isRewal ?? true;
                        if (isRenewable === false) {
                          return <div className="text-xs text-primary fw-600"><i className="bi bi-infinity me-1"></i>Non-Renewal</div>;
                        }
                        return <div className="text-xs text-danger fw-600">Renewal: {formatDate(sub.next_renewal_date)}</div>;
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const fb = sub.feedback;
                        const us = sub.usage;
                        const hasFeedback = fb === 'satisfied' || fb === 'disatisfied';
                        const hasUsage = us === 'using' || us === 'not using';
                        if (!hasFeedback && !hasUsage) {
                          return <span className="text-xs text-muted">—</span>;
                        }
                        return (
                          <div className="d-flex flex-column gap-1">
                            {hasFeedback && (
                              <div>
                                <span className="text-xs text-muted me-1">FB:</span>
                                <span className={`badge badge-soft-${fb === 'satisfied' ? 'success' : 'danger'}`}>
                                  {fb.toUpperCase()}
                                </span>
                              </div>
                            )}
                            {hasUsage && (
                              <div>
                                <span className="text-xs text-muted me-1">USE:</span>
                                <span>
                                  {us.toUpperCase()}
                                </span>
                              </div>
                            )}
                            {sub.last_followup_at && (
                              <div className="text-xs text-muted">
                                <i className="bi bi-clock me-1"></i>{formatDate(sub.last_followup_at)}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td>
                      {followupsLoading ? (
                        <div style={{ minWidth: 150 }} className="d-flex flex-column gap-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="spinner-border spinner-border-xs text-primary" role="status" style={{ width: 12, height: 12 }}></span>
                            <span className="text-xs text-muted">Loading followups…</span>
                          </div>
                          <div className="text-xs text-muted opacity-50">
                            <span className="fw-600 me-1">Next:</span>
                            <span className="text-decoration-underline" style={{ textDecorationStyle: 'dotted', textDecorationColor: 'currentColor' }}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                          </div>
                          <div className="text-xs text-muted opacity-50">
                            <span className="fw-600 me-1">Last:</span>
                            <span className="text-decoration-underline" style={{ textDecorationStyle: 'dotted', textDecorationColor: 'currentColor' }}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
                          </div>
                        </div>
                      ) : (() => {
                        const list = getFollowupsForSub(sub._id);
                        const pending = list.filter(f => f.schedule_followup === true && f.followup_date).length;
                        const done = list.filter(f => f.status === 'completed' || f.feedback === 'completed').length;
                        const nextF = getNextFollowup(list);
                        const lastF = getLastCompleted(list);
                        const now = Date.now();
                        const overdue = nextF && nextF._ts < now;
                        return (
                          <div style={{ minWidth: 150 }}>
                            <div className="d-flex flex-wrap gap-2 mb-1">
                              {pending > 0 && (
                                <span className="text-xs text-warning fw-600">{pending} Pending</span>
                              )}
                              {done > 0 && (
                                <span className="text-xs text-success fw-600">{done} Done</span>
                              )}
                              {pending === 0 && done === 0 && (
                                <span className="text-xs text-muted">No Follow-ups yet</span>
                              )}
                            </div>
                            <div className="d-flex flex-column gap-1">
                              {nextF ? (
                                <div className="text-xs">
                                  <span className="fw-600 me-1">Next:</span>
                                  <span className={overdue ? 'text-danger fw-600' : ''}>{formatDate(nextF.followup_date)} / {overdue ? 'Overdue' : 'Pending'}</span>
                                </div>
                              ) : (pending === 0 && done === 0 ? null : (
                                <div className="text-xs">
                                  <span className="fw-600 me-1">Next:</span>
                                  <span className="text-muted">— / None</span>
                                </div>
                              ))}
                              {lastF && (
                                <div className="text-xs">
                                  <span className="fw-600 me-1">Last:</span>
                                  <span>{formatDate(lastF.completedAt || lastF.updated_at || lastF.created_at)} / Completed</span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </td>

                    <td>
                      <div className="fw-600">{formatCurrency(sub.amount)}</div>
                    </td>
                    <td>
                      <span className={`badge badge-soft-${sub.status === 'active' ? 'success' : sub.status === 'expired' ? 'danger' : 'warning'}`}>
                        {sub.status?.toUpperCase()}
                      </span>
                    </td>
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        {(userRole === 'owner' || tableActions.edit !== false) && (
                          <button className="btn btn-sm btn-light" onClick={() => navigate(`/billing/subscriptions/edit/${sub._id}`)} title="Edit"><i className="bi bi-pencil"></i></button>
                        )}
                        {(userRole === 'owner' || tableActions.view !== false) && (
                          <button className="btn btn-sm btn-light text-primary" onClick={() => navigate(`/billing/subscriptions/view/${sub._id}`)} title="View"><i className="bi bi-eye"></i></button>
                        )}
                        {(userRole === 'owner' || tableActions.followup !== false) && (
                          <button className="btn btn-sm btn-light text-success" onClick={() => handleOpenFollowup(sub)} title="Add Follow-up"><i className="bi bi-chat-dots"></i></button>
                        )}
                        {(userRole === 'owner' || tableActions.renewal !== false) && (
                          <button className="btn btn-sm btn-light text-info" onClick={() => handleOpenRenewal(sub)} title="Renewal"><i className="bi bi-arrow-repeat"></i></button>
                        )}
                        {(userRole === 'owner' || tableActions.delete !== false) && can('deleteSales') && (
                          <button className="btn btn-sm btn-light text-danger" onClick={() => handleDelete(sub)} title="Delete"><i className="bi bi-trash"></i></button>
                        )}
                       
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {total > limit && (
          <div className="card-footer bg-transparent border-0 py-4 px-4">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="text-muted small">Showing {(page-1)*limit + 1} to {Math.min(page*limit, total)} of {total} results</div>
              <CompactPagination
                page={page}
                total={total}
                limit={limit}
                onPageChange={setPage}
              />
            </div>
          </div>
        )}
      </div>

      {/* Follow-up Modal */}
      {showFollowupModal && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content border-0">
              <div className="modal-header border-0">
                <h5 className="modal-title fw-bold">
                  {followupData.schedule_followup ? 'Schedule Follow-up' : 'Add Feedback & Follow-up'}
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body p-0">
                <div className="row g-0">
                  <div className="col-md-6 border-end">
                    <form onSubmit={handleFollowupSubmit} className="p-4">
                      <div className="mb-3">
                        <label className="form-label text-muted small fw-600">COMMENT</label>
                        <textarea className="form-control" rows="3" value={followupData.comment} onChange={e => setFollowupData({...followupData, comment: e.target.value})} placeholder="Enter notes about this customer interaction..."></textarea>
                      </div>
                      <div className="form-check mb-3">
                        <input 
                          type="checkbox" 
                          className="form-check-input" 
                          id="scheduleCheck" 
                          checked={followupData.schedule_followup} 
                          onChange={e => setFollowupData({...followupData, schedule_followup: e.target.checked})} 
                        />
                        <label className="form-check-label small fw-600" htmlFor="scheduleCheck">Schedule Follow-up</label>
                      </div>
                      {followupData.schedule_followup && (
                        <div className="mb-3">
                          <label className="form-label text-muted small fw-600">FOLLOW-UP DATE & TIME</label>
                          <input 
                            type="datetime-local" 
                            className="form-control" 
                            value={followupData.followup_date} 
                            onChange={e => setFollowupData({...followupData, followup_date: e.target.value})} 
                            required 
                          />
                        </div>
                      )}
                      {!followupData.schedule_followup && (
                        <>
                          <div className="mb-3">
                            <label className="form-label text-muted small fw-600">FEEDBACK</label>
                            <select className="form-select" value={followupData.feedback} onChange={e => setFollowupData({...followupData, feedback: e.target.value})} required>
                              <option value="satisfied">Satisfied</option>
                              <option value="disatisfied">Disatisfied</option>
                            </select>
                          </div>
                          <div className="mb-3">
                            <label className="form-label text-muted small fw-600">USAGE</label>
                            <select className="form-select" value={followupData.usage} onChange={e => setFollowupData({...followupData, usage: e.target.value})} required>
                              <option value="using">Using</option>
                              <option value="not using">Not Using</option>
                            </select>
                          </div>
                        </>
                      )}
                      <div className="d-flex gap-2 mt-4">
                        <button type="submit" className="btn btn-primary px-4">
                          {followupData.schedule_followup ? 'Schedule Follow-up' : 'Save Feedback'}
                        </button>
                        <button type="button" className="btn btn-light px-4" onClick={() => setShowModal(false)}>Cancel</button>
                      </div>
                    </form>
                  </div>
                  <div className="col-md-6 bg-light">
                    <div className="p-4">
                      <h6 className="fw-700 mb-3 small text-uppercase text-muted">Feedback History</h6>
                      <div className="overflow-auto" style={{ maxHeight: '400px' }}>
                        {feedbackHistory.length > 0 ? (
                          feedbackHistory.slice().reverse().map((h, i) => (
                            <div key={i} className="card border-0 shadow-sm mb-3 rounded-3">
                              <div className="card-body p-3">
                                <div className="d-flex justify-content-between mb-2">
                                  <span className={`badge badge-soft-${h.feedback === 'satisfied' ? 'success' : 'danger'} text-xs`}>
                                    {h.feedback?.toUpperCase()}
                                  </span>
                                  <span className="text-xs text-muted">{formatDate(h.created_at || h.date)}</span>
                                </div>
                                <div className="text-xs fw-600 mb-1">Usage: <span className="text-primary">{h.usage}</span></div>
                                <div className="text-xs text-dark italic">"{h.comment || 'No comment provided'}"</div>
                                {h.schedule_followup && (
                                  <div className="mt-2 pt-2 border-top text-xs text-muted">
                                    <i className="bi bi-calendar-event me-1"></i>
                                    Next Follow-up: <span className="fw-600 text-info">{formatDate(h.followup_date)}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-5 text-muted small">
                            <i className="bi bi-chat-left-text d-block h4 mb-2"></i>
                            No feedback history found.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Renewal Modal */}
      <RenewalModal 
        show={showRenewalModal} 
        onClose={() => setShowRenewalModal(false)} 
        subscription={selectedSub} 
        onRenewalSuccess={fetchSubscriptions}
      />

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg">
              <div className="modal-header border-0 p-4 pb-0">
                <h5 className="modal-title fw-800">Upload Sales</h5>
                <button type="button" className="btn-close" onClick={() => setShowUploadModal(false)}></button>
              </div>
              <div className="modal-body p-4">
                <div className="alert alert-info small d-flex justify-content-between align-items-center">
                  <span>
                    <i className="bi bi-info-circle-fill me-2"></i>
                    Upload up to 5000 records. Required: customerId, product_name.
                  </span>
                  <button type="button" className="btn btn-sm btn-link p-0 text-decoration-none fw-700" onClick={downloadSample}>
                    <i className="bi bi-download me-1"></i> Sample Format
                  </button>
                </div>
                <input 
                  type="file" 
                  className="form-control mb-3" 
                  accept=".xlsx, .xls, .csv"
                  onChange={handleFileUpload}
                />
                {uploadRecords.length > 0 && (
                  <div className="text-success small fw-600 mb-2">
                    {uploadRecords.length} records detected.
                  </div>
                )}
                {uploadRecords.length > 5000 && (
                  <div className="text-danger small fw-600">
                    Warning: Records exceed the 5000 limit.
                  </div>
                )}
              </div>
              <div className="modal-footer border-0 p-4 pt-0">
                <button type="button" className="btn btn-light px-4" onClick={() => setShowUploadModal(false)}>Cancel</button>
                <button 
                  type="button" 
                  className="btn btn-primary px-4" 
                  disabled={uploadRecords.length === 0 || uploadRecords.length > 5000 || isUploading}
                  onClick={handleUploadSubmit}
                >
                  {isUploading ? 'Starting...' : 'Start Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionList;
